/**
 * Data container for the top-level "Units" view mode. Fetches the account-wide units list,
 * normalizes each unit (including its address lines), and hands the rows to the shared
 * UnitsTableView. Owns the archive modal opened from a row's overflow menu.
 */
import { useState } from 'react';

import {
  AccountViewListingGroupType,
  type ListingAlias,
  type RentalManagerAddressDto,
} from '@zg-rentals/ts-rental-manager-api-client';

import { useFlexiblePropertiesSearch } from '../../../contexts/FlexiblePropertiesSearchContext';
import { useGetPropertyTableUnitsList } from '../../../hooks/queries/propertyTable';
import { getUnitAddressLines } from '../../../utils/flexiblePropertyCardUtils';
import propertyUtils from '../../../utils/propertyUtils';

import PropertiesConstants from '../../../constants/PropertiesConstants';

import FlexibleArchivePropertyModal, {
  ARCHIVE_TARGET_NOUN,
} from '../../../components/modals/FlexibleArchivePropertyModal';
import PropertyDetailsModal from '../../../components/modals/PropertyDetailsModal';

import UnitsTableView, { UNIT_TABLE_VARIANT } from './UnitsTableView';

import type { UnitTableRow } from './UnitsTableView';

type ArchiveTarget = {
  listingAlias: ListingAlias;
  address: RentalManagerAddressDto;
  unitNumber?: string;
};

const UnitsListTable = () => {
  const { searchText, includeArchived } = useFlexiblePropertiesSearch();
  const [archiveTarget, setArchiveTarget] = useState<ArchiveTarget | null>(null);
  const [editListingAlias, setEditListingAlias] = useState<ListingAlias | null>(null);

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = useGetPropertyTableUnitsList({
    searchText,
    includeArchived,
    includeUnarchived: !includeArchived,
    limit: PropertiesConstants.PROPERTIES_MULTIPLEX_LIMIT_DEFAULT,
  });

  const units = data.units ?? [];

  const rows: Array<UnitTableRow> = units.map((unit) => {
    const unitLabel = unit.address.unit ? `Unit ${unit.address.unit}` : '—';
    const { streetLine, cityStateZip } = getUnitAddressLines(unit.address);

    return {
      listingAlias: unit.listingAlias,
      address: unit.address,
      photoKey: unit.photoKey,
      unitLabel,
      unitAccessibleLabel: unit.address.unit ? unitLabel : 'Unit',
      unitInfo: propertyUtils.formatUnitInfo(unit.bedroomCount, unit.bathroomCount, unit.squareFeet),
      overflowLabel: unitLabel,
      unitNumber: unit.address.unit,
      rent: unit.rent,
      isTotalMonthlyPrice: unit.isTotalMonthlyPrice,
      isMultifamily: unit.isMultifamily,
      addressLines: { streetLine, cityStateZip },
    };
  });

  return (
    <>
      <UnitsTableView
        variant={UNIT_TABLE_VARIANT.LIST}
        rows={rows}
        isLoading={isFetching && units.length === 0}
        isFetching={isFetching}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        onArchive={(listingAlias, address, unitNumber) => setArchiveTarget({ listingAlias, address, unitNumber })}
        onEdit={setEditListingAlias}
      />

      {archiveTarget && (
        <FlexibleArchivePropertyModal
          onClose={() => setArchiveTarget(null)}
          isArchived={includeArchived}
          listingAlias={archiveTarget.listingAlias}
          groupId=""
          address={archiveTarget.address}
          targetNoun={ARCHIVE_TARGET_NOUN.UNIT}
          listingType={AccountViewListingGroupType.LISTING}
          unitNumber={archiveTarget.unitNumber}
        />
      )}

      {editListingAlias && (
        <PropertyDetailsModal
          isOpen={true}
          listingAlias={editListingAlias}
          submitLabel="Save changes"
          onClose={() => setEditListingAlias(null)}
          onConfirm={() => setEditListingAlias(null)}
        />
      )}
    </>
  );
};

export default UnitsListTable;
